import React, {useCallback} from 'react';
import {EIconSmallButton} from 'webapp-lib/Components';

import {Close} from '_/assets/images';
import {memo} from '_/utilities/memo';
import {Color} from '_/utilities/types';

import {StyledTag, TagNameWrapper} from './styles';

export interface BasicProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /**
   * If "true", the styling of the component changes to the alternative tag style
   */
  alternativeStyle?: false;
  /**
   *  inline gets passed to the components style and causes the element to be an inline-block
   */
  inline?: boolean;
  /**
   * Gets passed to the "class" attribute of the underlying "HTMLElement"
   */
  className?: string;
  /**
   * Will be rendered as the "label" and "title" attributes of the "input"
   */
  tagName: string;
  /**
   * Determines the "background-color" and "border-color" of the checkbox when it is checked
   */
  color: Color;
  /**
   * Will be rendered as the icon of the "Tag", if provided
   */
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  /**
   * Gets called with the new state when user changes the state of the "Tag"
   */
  onChange?: (arg0: boolean) => void;
  /**
   * If provided, a "Button" component with "close.svg" icon will be rendered
   */
  onClose?: () => void;
  /**
   * If set true, tag size will depend on display text
   */
  fitToMultiLines?: boolean;
}

export interface AlternativeProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /**
   * If "true", the styling of the component changes to the alternative tag style
   */
  alternativeStyle: true;
  /**
   *  inline gets passed to the components style and causes the element to be an inline-block
   */
  inline?: boolean;
  /**
   * Gets passed to the "class" attribute of the underlying "HTMLElement"
   */
  className?: string;
  /**
   * Will be rendered as the "label" and "title" attributes of the "input"
   */
  tagName: string;
  /**
   * Will be rendered as the icon of the "Tag", if provided
   */
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  /**
   * Gets called with the new state when user changes the state of the "Tag"
   */
  onChange?: (arg0: boolean) => void;
  /**
   * If provided, a "Button" component with "close.svg" icon will be rendered
   */
  onClose?: () => void;
  /**
   * If set true, tag size will depend on display text
   */
  fitToMultiLines?: boolean;
}

export type Props = BasicProps | AlternativeProps;

export function TagComponent({
  alternativeStyle,
  className,
  tagName,
  Icon,
  onChange,
  onClose,
  inline: _,
  fitToMultiLines: _fitToMultiLines,
  ...props
}: Props): React.ReactElement {
  const onInputChange = useCallback(
    ({target: {checked: checked_}}: any) =>
      !props.disabled && onChange?.(checked_),
    [onChange, props.disabled]
  );

  return (
    <label className={className} tabIndex={alternativeStyle ? -1 : undefined}>
      <input
        readOnly={!onChange}
        tabIndex={alternativeStyle ? -1 : undefined}
        title={tagName}
        type='checkbox'
        onChange={onChange ? onInputChange : undefined}
        {...props}
      />
      <TagNameWrapper>{tagName}</TagNameWrapper>
      {Icon ? <Icon /> : null}
      {onClose ? (
        <EIconSmallButton onClick={onClose}>
          <Close />
        </EIconSmallButton>
      ) : null}
    </label>
  );
}

export default memo(TagComponent, StyledTag);
